import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box, Divider, List, Typography } from '@mui/material';
import { Grid } from '@mui/system';

import { TooltipIconButton } from '@paragon/client-tools';
import { Member } from '@paragon/contract-library';
import { LineSegment2D } from '@paragon/geometry-library';
import { hasTrussDesignPermission as hasTrussDesignPermissionSelector } from '@paragon/login-tools';
import { getDirectionalBaseAndEndTerms } from '@paragon/relative-geometry';
import { calculateMemberMidline } from '@paragon/three-shared';

import { RootState } from '../../../../../store';
import { setMemberBevelCut, setMemberBottomBevelCut } from '../../../../../store/actions/editMemberActions';
import { startSelectMemberCut } from '../../../../../store/actions/insertActions';
import { getActiveComponentDesign } from '../../../../../store/selectors/getActiveComponentDesignSelector';
import { getReferenceLinesForActiveComponent } from '../../../../../store/selectors/getReferenceLinesForActiveComponent';
import { getPickingObjectInfo } from '../../../../../store/selectors/pickingSelectors';
import { propertiesShouldBeViewOnly } from '../../../../../store/selectors/viewOnlyModeSelectors';
import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { BevelCutProperties } from '../../elements/BevelCutProperties';
import { BottomBevelCutProperties } from '../../elements/BottomBevelCutProperties';
import { CutRow } from '../CutRow';
import { PropertiesPanelSubtitle } from '../PropertiesPanelSubtitle';

interface Props {
  disabled?: boolean;
  members: readonly Member[];
}

function getMemberCutTargetInfo(state: RootState) {
  const info = getPickingObjectInfo(state);
  return info && info.type === 'pickingReferenceLine' && info.targetInfo.type === 'memberCut' ? info.targetInfo : null;
}

export function MemberCutsSubPanel({ disabled, members }: Props) {
  const singleMember = members.length === 1 ? members[0] : null;

  const dispatch = useAppDispatch();
  const isViewOnly = useAppSelector(propertiesShouldBeViewOnly);
  const hasTrussDesignPermission = useAppSelector(hasTrussDesignPermissionSelector);

  const pickingType = useAppSelector((state) => getPickingObjectInfo(state)?.type);
  const memberCutTargetInfo = useAppSelector(getMemberCutTargetInfo);

  const pickingLine = pickingType === 'pickingReferenceLine';
  const insertingNewCut = !!memberCutTargetInfo && !memberCutTargetInfo.replacingLineGuid;

  const addDisabled = disabled || pickingLine || insertingNewCut;
  const getAddNewCutAction = (isForBaseCut: boolean) => () =>
    dispatch(startSelectMemberCut(singleMember!, isForBaseCut));

  const componentDesign = useAppSelector(getActiveComponentDesign);
  const referenceLines = useAppSelector(getReferenceLinesForActiveComponent);

  const memberMidlineAlignedWithCuts = useAppSelector((state) => {
    if (!singleMember) return null;
    const componentDesign = getActiveComponentDesign(state)?.component ?? null;
    return componentDesign && calculateMemberMidline(singleMember, componentDesign);
  });

  if (isViewOnly || !hasTrussDesignPermission || componentDesign == null) return null;

  const memberDirectionAlignedWithCuts =
    memberMidlineAlignedWithCuts && LineSegment2D.fromSegmentObject(memberMidlineAlignedWithCuts).direction;
  const { baseTerm, endTerm } = getDirectionalBaseAndEndTerms(memberDirectionAlignedWithCuts);

  const baseCuts =
    singleMember && singleMember.relativeGeometry
      ? singleMember.relativeGeometry.baseCutGuids.map((baseCutGuid, index) => {
          if (!referenceLines) return undefined;
          const cutLine = referenceLines.find((line) => line.guid === baseCutGuid);
          if (!cutLine) return undefined;
          return (
            <CutRow
              member={singleMember}
              isForBaseCut
              cutEndTerm={baseTerm}
              index={index}
              cutGuid={baseCutGuid}
              cutLine={cutLine}
              disabled={disabled ?? false}
              key={`${baseCutGuid}-${index}`}
            />
          );
        })
      : [];

  if (singleMember && pickingLine && insertingNewCut && memberCutTargetInfo && memberCutTargetInfo.isForBaseCut) {
    baseCuts.push(
      <CutRow
        member={singleMember}
        isForBaseCut
        cutEndTerm={baseTerm}
        index={baseCuts.length}
        disabled={disabled ?? false}
        key="new-base-cut"
      />,
    );
  }

  const baseCutsSubsection = singleMember && (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex' }}>
        <Typography sx={{ alignSelf: 'center', marginRight: 'auto' }} variant="subtitle1">
          {baseTerm} Cuts
        </Typography>

        {singleMember.relativeGeometry && (
          <TooltipIconButton
            title={`Add ${baseTerm} Cut`}
            disabled={addDisabled}
            onClick={getAddNewCutAction(true)}
            sx={{ m: 1 }}
          >
            <AddIcon />
          </TooltipIconButton>
        )}
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>{baseCuts}</Box>
      </Box>
    </Box>
  );

  const endCuts =
    singleMember && singleMember.relativeGeometry
      ? singleMember.relativeGeometry.endCutGuids.map((endCutGuid, index) => {
          if (!referenceLines) return undefined;
          const cutLine = referenceLines.find((line) => line.guid === endCutGuid);
          if (!cutLine) return undefined;
          return (
            <CutRow
              member={singleMember}
              isForBaseCut={false}
              cutEndTerm={endTerm}
              index={index}
              cutGuid={endCutGuid}
              cutLine={cutLine}
              disabled={disabled ?? false}
              key={`${endCutGuid}-${index}`}
            />
          );
        })
      : [];

  if (singleMember && pickingLine && insertingNewCut && memberCutTargetInfo && !memberCutTargetInfo.isForBaseCut) {
    endCuts.push(
      <CutRow
        member={singleMember}
        isForBaseCut={false}
        cutEndTerm={endTerm}
        index={endCuts.length}
        disabled={disabled ?? false}
        key="new-end-cut"
      />,
    );
  }

  const endCutsSubsection = singleMember && (
    <Box sx={{ mt: 1 }}>
      <PropertiesPanelSubtitle
        endButton={
          singleMember.relativeGeometry && (
            <TooltipIconButton
              title={`Add ${endTerm} Cut`}
              disabled={addDisabled}
              onClick={getAddNewCutAction(false)}
              sx={{ m: 1 }}
            >
              <AddIcon />
            </TooltipIconButton>
          )
        }
      >
        {endTerm} Cuts
      </PropertiesPanelSubtitle>
      <Box sx={{ mt: 0.5 }}>
        <List>{endCuts}</List>
      </Box>
    </Box>
  );

  const flipBaseAndEndCutsSubsections = baseTerm === 'Bottom' || baseTerm === 'Right';
  const [firstCutsSubsection, secondCutsSubsection] = flipBaseAndEndCutsSubsections
    ? [endCutsSubsection, baseCutsSubsection]
    : [baseCutsSubsection, endCutsSubsection];

  return (
    <>
      {hasTrussDesignPermission && !isViewOnly && (
        <>
          {singleMember && (
            <Box sx={{ mb: 2 }}>
              {firstCutsSubsection}
              {secondCutsSubsection}
            </Box>
          )}

          <PropertiesPanelSubtitle>Bevel Cuts</PropertiesPanelSubtitle>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {singleMember && (
              <>
                <Grid size={6}>
                  <Box sx={{ m: 0, display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center' }}>
                    <BevelCutProperties
                      sx={{ m: 0 }}
                      bevelCut={singleMember.leftBevelCut}
                      propertyName="Left"
                      disabled={disabled ?? false}
                      onEditBevelCut={async (newBevelCut) =>
                        await dispatch(setMemberBevelCut(componentDesign!.guid, singleMember, 'Left', newBevelCut))
                      }
                    />
                  </Box>
                </Grid>
                <Grid size={6}>
                  <Box sx={{ m: 0, display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center' }}>
                    <BevelCutProperties
                      sx={{ m: 0 }}
                      bevelCut={singleMember.rightBevelCut}
                      propertyName="Right"
                      disabled={disabled ?? false}
                      onEditBevelCut={async (newBevelCut) =>
                        await dispatch(setMemberBevelCut(componentDesign!.guid, singleMember, 'Right', newBevelCut))
                      }
                    />
                  </Box>
                </Grid>
              </>
            )}
            <Grid size={6}>
              <Box sx={{ m: 0, display: 'flex', flex: 1, flexDirection: 'column', alignItems: 'center' }}>
                <BottomBevelCutProperties
                  sx={{ m: 0 }}
                  bevelCuts={members.map((member) => member.bottomBevelCut)}
                  propertyName="Bottom"
                  disabled={disabled ?? false}
                  onEditBevelCut={async (newBevelCut) =>
                    await dispatch(setMemberBottomBevelCut(componentDesign!.guid, members, newBevelCut))
                  }
                />
              </Box>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
